import { useState } from 'react';

import { toast } from 'react-toastify';
import styled from 'styled-components';

import {
    GetMicrostoreSitesQuery,
    useReplaceMicrostoreColumnTemplateMutation,
    useGetMicrostoreColumnTemplatesQuery,
    GetMicrostoreSitesDocument,
    MicrostoreColumnTemplateState,
    TotadminPermission,
} from 'data/__generated__';

import { Loader } from 'components/Loader';
import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { TotemSelect } from 'components/TotemSelect';

export function ReplaceMicrostoreColumnTemplatePopup({
    isOpen,
    setMicrostoreColumnToReplaceTemplate,
    column,
}: {
    isOpen: boolean;
    setMicrostoreColumnToReplaceTemplate: (
        column:
            | GetMicrostoreSitesQuery['microstoreSitesWithColumnGroups'][number]['microstoreColumnGroups'][number]['columns'][number]
            | null,
    ) => void;
    column: GetMicrostoreSitesQuery['microstoreSitesWithColumnGroups'][number]['microstoreColumnGroups'][number]['columns'][number];
}) {
    const [newMicrostoreColumnTemplateId, setNewMicrostoreColumnTemplateId] = useState<string | null>(null);

    const {
        loading: microstoreColumnTemplateLoading,
        data: microstoreColumnTemplateData,
        error: microstoreColumnTemplateError,
    } = useGetMicrostoreColumnTemplatesQuery();

    const [replaceMicrostoreColumnTemplate, { loading: replaceLoading }] = useReplaceMicrostoreColumnTemplateMutation({
        refetchQueries: [GetMicrostoreSitesDocument],
    });

    if (microstoreColumnTemplateLoading) {
        return (
            <TotemPopup
                title="Changer le template de colonne"
                isOpen={isOpen}
                setIsOpen={() => setMicrostoreColumnToReplaceTemplate(null)}
                contentOverflow="visible"
            >
                <Loader />
            </TotemPopup>
        );
    }

    if (microstoreColumnTemplateError || !microstoreColumnTemplateData) {
        throw new Error('Une erreur est survenue lors de la récupération des templates de colonne microstore');
    }

    async function handleSubmit() {
        if (!newMicrostoreColumnTemplateId || replaceLoading) {
            if (!newMicrostoreColumnTemplateId) {
                toast.error('Veuillez sélectionner un nouveau template', { autoClose: false });
            }
            return;
        }

        const { data } = await replaceMicrostoreColumnTemplate({
            variables: {
                replaceMicrostoreColumnTemplateInput: {
                    columnId: column._id,
                    newMicrostoreColumnTemplateId,
                },
            },
        });

        if (!data) {
            throw new Error('Une erreur est survenue lors du changement de template de colonne');
        }

        const {
            replaceMicrostoreColumnTemplateMutation: { errors },
        } = data;

        if (errors.length) {
            errors.forEach((error: string, index: number) => {
                toast.error(<span key={index}>Erreur : {error}</span>, { autoClose: false });
            });

            if (errors.length > 1) {
                toast.info('Cliquez pour fermer toutes les notifications', {
                    autoClose: false,
                    onClick: () => toast.dismiss(),
                });
            }
        } else {
            toast.success('Template de colonne changé avec succès !');
        }

        setMicrostoreColumnToReplaceTemplate(null);
    }

    const { microstoreColumnTemplatesQuery: microstoreColumnTemplates } = microstoreColumnTemplateData;

    const microstoreColumnTemplateOptions =
        microstoreColumnTemplates
            .filter(({ _id }) => _id !== column.microstoreColumnTemplateId) // Exclude current template
            .filter(
                (microstoreColumnTemplate) => microstoreColumnTemplate.state === MicrostoreColumnTemplateState.Regular,
            )
            .map(({ _id: microstoreColumnTemplateId, name }) => ({
                value: microstoreColumnTemplateId,
                label: name,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)) || [];

    const currentTemplate = microstoreColumnTemplates.find(({ _id }) => _id === column.microstoreColumnTemplateId);

    return (
        <TotemPopup
            title="Changer le template de colonne"
            isOpen={isOpen}
            setIsOpen={() => setMicrostoreColumnToReplaceTemplate(null)}
            contentOverflow="visible"
        >
            <SubmitContainer>
                <InfoLabel>Template actuel :</InfoLabel>
                <InfoValue>{currentTemplate?.name || 'Template inconnu'}</InfoValue>

                <TotemSelect
                    label="Nouveau template de colonne"
                    placeholder="Sélectionner le nouveau template"
                    value={newMicrostoreColumnTemplateId}
                    options={microstoreColumnTemplateOptions}
                    onChange={(value) => {
                        if (value) {
                            setNewMicrostoreColumnTemplateId(value);
                        }
                    }}
                />

                <TotemPrimaryLoadingButton
                    loading={replaceLoading}
                    onClick={handleSubmit}
                    disabled={!newMicrostoreColumnTemplateId}
                    useIcon={false}
                    label="Confirmer le changement"
                    requiredPermissions={[TotadminPermission.MicrostoreColumnsManage]}
                />
            </SubmitContainer>
        </TotemPopup>
    );
}

const SubmitContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;

    > :not(:first-child) {
        margin-top: 15px;
    }
`;

const InfoLabel = styled.div`
    font-weight: bold;
    color: #333;
`;

const InfoValue = styled.div`
    color: #666;
`;
